import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  TextField,
  MenuItem,
  Tooltip,
  Checkbox,
} from "@mui/material";
import {
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiX,
  FiTrash2,
  FiEdit2,
  FiMessageSquare,
} from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { remindersAPI } from "../services/api";
import TableLoader from "../components/TableLoader";
import { colors } from "../theme/modernTheme";
import { showSuccess, showError } from "../utils/toast";
import { formatDateForDisplay } from "../utils/dateUtils";
import { useMultiSelect } from "../hooks/useMultiSelect";
import BulkActionToolbar from "../components/BulkActionToolbar";
import BulkDeleteDialog from "../components/BulkDeleteDialog";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import CommentModal from "../components/CommentModal";
import { useLoading } from "../context/LoadingContext";
import { useAuth } from "../context/AuthContext";

export default function Reminders() {
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);

  const canDelete = user?.role === "ADMIN";

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Multi-select state
  const multiSelect = useMultiSelect(reminders, totalCount, "id");
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // Single delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Comment modal state
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);

  // Temporary filter states (not applied until user clicks Apply)
  const [searchInput, setSearchInput] = useState("");
  const [statusInput, setStatusInput] = useState("");
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");

  // Applied filter states (used for API calls)
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Initialize filters from URL parameters BEFORE first load
  useEffect(() => {
    const statusParam = searchParams.get("status");

    if (statusParam) {
      setStatusInput(statusParam);
      setStatusFilter(statusParam);
    }
  }, []);

  useEffect(() => {
    loadReminders();
  }, [page, statusFilter, startDate, endDate, search]);

  const loadReminders = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (statusFilter) params.status = statusFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (search) params.search = search;

      const response = await remindersAPI.getAll(params);
      setReminders(response.data);
      setTotalCount(response.pagination?.total || response.data.length);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setSearch(searchInput);
    setStatusFilter(statusInput);
    setStartDate(startDateInput);
    setEndDate(endDateInput);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setStatusInput("");
    setStartDateInput("");
    setEndDateInput("");
    setSearch("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  const hasActiveFilters =
    searchInput || statusInput || startDateInput || endDateInput;

  // Check if filters have changed from applied state
  const hasFilterChanges =
    searchInput !== search ||
    statusInput !== statusFilter ||
    startDateInput !== startDate ||
    endDateInput !== endDate;

  const handleDeleteClick = (reminder) => {
    setReminderToDelete(reminder);
    setDeleteDialogOpen(true);
  };

  const handleCommentClick = (reminder) => {
    setSelectedReminder(reminder);
    setCommentModalOpen(true);
  };

  const getContactName = (reminder) => {
    if (reminder.first_name) {
      return `${reminder.first_name} ${reminder.last_name || ""}`.trim();
    }
    return reminder.organization_name || "Unknown Contact";
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await remindersAPI.delete(reminderToDelete.id);
      multiSelect.removeFromSelection(reminderToDelete.id);
      setDeleteDialogOpen(false);
      setReminderToDelete(null);
      loadReminders();
      showSuccess("Reminder deleted successfully");
    } catch (error) {
      showError(error.error?.message || "Failed to delete reminder");
    } finally {
      setDeleting(false);
    }
  };

  const handleReminderClick = (reminder) => {
    navigate(`/reminders/${reminder.id}`);
  };

  const handleBulkDelete = async () => {
    try {
      showLoading();
      const selectionData = multiSelect.getSelectionData();

      // Validate that items are selected
      if (
        !selectionData.selectAll &&
        (!selectionData.ids || selectionData.ids.length === 0)
      ) {
        showError("No reminders selected");
        hideLoading();
        return;
      }

      // Build filters object from current filter state
      const filters = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      await remindersAPI.bulkDelete({
        ...selectionData,
        filters,
      });

      setBulkDeleteDialogOpen(false);
      multiSelect.clearSelection();
      loadReminders();
      showSuccess(
        `Successfully deleted ${multiSelect.selectedCount} reminder(s)`
      );
    } catch (error) {
      showError(error?.error?.message || "Failed to delete reminders");
    } finally {
      hideLoading();
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.2}
        mt={2}>
        <Typography
          variant="h4"
          sx={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: colors.text.primary,
          }}>
          Reminders
        </Typography>
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "4px",
          boxShadow: "none",
          opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
          pointerEvents: multiSelect.selectedCount > 0 ? "none" : "auto",
        }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search reminders..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleFilterKeyPress}
              disabled={multiSelect.selectedCount > 0}
              InputProps={{
                endAdornment: searchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput("");
                      setSearch("");
                      setPage(0);
                    }}
                    disabled={multiSelect.selectedCount > 0}
                    sx={{ padding: "4px" }}>
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch
                    style={{ marginRight: 8, color: colors.text.secondary }}
                  />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.background.paper,
                  borderRadius: "4px",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                  sx: {
                    "& .MuiMenu-list": {
                      padding: "0px",
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
              }}>
              <MenuItem
                value=""
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                All Status
              </MenuItem>
              <MenuItem
                value="ACTIVE"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Active
              </MenuItem>
              <MenuItem
                value="CANCELLED"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Cancelled
              </MenuItem>
              <MenuItem
                value="COMPLETED"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Completed
              </MenuItem>
              <MenuItem
                value="OVERDUE"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Overdue
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Next Date from"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                onClick: (e) => e.target.showPicker?.(),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Next Date to"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                onClick: (e) => e.target.showPicker?.(),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                disableRipple
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges || multiSelect.selectedCount > 0}
                sx={{
                  backgroundColor: "transparent",
                  color: colors.primary.main,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 510,
                  p: "8px",
                  mr: -1,
                  "&:disabled": {
                    color: colors.text.disabled,
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}>
                Apply
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters || multiSelect.selectedCount > 0}
                sx={{
                  backgroundColor: "transparent",
                  color: colors.primary.main,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 510,
                  p: "8px",
                  "&:disabled": {
                    color: colors.text.disabled,
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}>
                Clear
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}>
              <IconButton
                size="small"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0 || multiSelect.selectedCount > 0}
                sx={{
                  borderRadius: 1,
                  marginRight: -2,
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                  },
                }}>
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: "100px",
                  textAlign: "center",
                }}>
                Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setPage(
                    Math.min(Math.ceil(totalCount / rowsPerPage) - 1, page + 1)
                  )
                }
                disabled={
                  page >= Math.ceil(totalCount / rowsPerPage) - 1 ||
                  multiSelect.selectedCount > 0
                }
                sx={{
                  borderRadius: 1,
                  marginLeft: -2,
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                  },
                }}>
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={multiSelect.selectedCount}
        onClearSelection={multiSelect.clearSelection}
        onDelete={() => setBulkDeleteDialogOpen(true)}
        canDelete={canDelete}
        itemName="reminders"
      />

      {loading ? (
        <TableLoader message="Loading reminders..." />
      ) : reminders.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: colors.text.secondary,
          }}>
          <Typography variant="body1">No reminders found</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            border: `1px solid ${colors.border.main}`,
            borderRadius: "4px",
            overflowX: "auto",
          }}>
          <Table>
            <TableHead sx={{ borderBottom: "1px solid #E5E7EB" }}>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ width: "2%", py: 1, px: 1 }}>
                  <Checkbox
                    size="medium"
                    indeterminate={multiSelect.isSomeCurrentPageSelected}
                    checked={multiSelect.isAllCurrentPageSelected}
                    onChange={multiSelect.toggleSelectAll}
                    sx={{
                      padding: "4px",
                      color: colors.text.secondary,
                      "& .MuiSvgIcon-root": { fontSize: 28 },
                      transform: "scale(1.2)",
                      "&.Mui-checked": {
                        color: colors.primary.main,
                      },
                      "&.MuiCheckbox-indeterminate": {
                        color: colors.primary.main,
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    paddingLeft: 0,
                    width: "120px",
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  Start Date
                </TableCell>
                <TableCell
                  sx={{
                    width: "150px",
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  Next Date
                </TableCell>
                <TableCell
                  sx={{
                    width: "200px",
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  Contact
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  Amount
                </TableCell>
                <TableCell
                  sx={{
                    width: "120px",
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  Reminder Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  {multiSelect.selectedCount > 0 ? "" : "Actions"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reminders.map((reminder) => {
                const isSelected = multiSelect.isSelected(reminder);
                return (
                  <TableRow
                    key={reminder.id}
                    selected={isSelected}
                    onClick={() => handleReminderClick(reminder)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { backgroundColor: colors.action.row },
                      "&:not(:last-of-type)": {
                        borderBottom: `1px solid ${colors.border.main}`,
                      },
                      ...(isSelected && {
                        backgroundColor: `${colors.primary.main}08`,
                        "&:hover": {
                          backgroundColor: `${colors.primary.main}12`,
                        },
                      }),
                    }}>
                    <TableCell
                      padding="checkbox"
                      sx={{ py: 1.5, px: 1 }}
                      onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="medium"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          multiSelect.toggleSelect(reminder);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        sx={{
                          padding: "4px",
                          "& .MuiSvgIcon-root": { fontSize: 28 },
                          transform: "scale(1.2)",
                          color: colors.text.secondary,
                          "&.Mui-checked": {
                            color: colors.primary.main,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        paddingLeft: 0,
                        width: "150px",
                        fontSize: "0.875rem",
                        color: colors.text.primary,
                        py: 1,
                      }}>
                      {reminder.recurring_start_date
                        ? formatDateForDisplay(reminder.recurring_start_date)
                        : "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "150px",
                        fontSize: "0.875rem",
                        color: colors.text.primary,
                        py: 1,
                      }}>
                      {reminder.next_recurring_date
                        ? formatDateForDisplay(reminder.next_recurring_date)
                        : "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: "180px",
                        fontSize: "0.875rem",
                        color: colors.text.primary,
                        py: 1,
                      }}>
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.875rem",
                        }}>
                        <Tooltip
                          title={
                            reminder.first_name
                              ? `${reminder.first_name} ${
                                  reminder.last_name || ""
                                }`
                              : reminder.organization_name
                          }
                          arrow
                          placement="top">
                          <span>
                            {reminder.first_name
                              ? `${reminder.first_name} ${
                                  reminder.last_name || ""
                                }`
                              : reminder.organization_name}
                          </span>
                        </Tooltip>
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "100px",
                        fontSize: "0.875rem",
                        color: colors.text.primary,
                        py: 1,
                      }}>
                      {reminder.amount
                        ? `₹${parseFloat(reminder.amount).toLocaleString()}`
                        : "-"}
                    </TableCell>
                    <TableCell sx={{ width: "100px", py: 1 }}>
                      <Chip
                        label={reminder.reminder_status}
                        size="small"
                        sx={{
                          backgroundColor:
                            reminder.reminder_status === "ACTIVE"
                              ? `${colors.success.main}15`
                              : reminder.reminder_status === "PAUSED"
                              ? `${colors.warning.main}15`
                              : reminder.reminder_status === "CANCELLED" ||
                                reminder.reminder_status === "COMPLETED"
                              ? `${colors.error.main}15`
                              : reminder.reminder_status === "SENT"
                              ? `${colors.success.main}15`
                              : reminder.reminder_status === "OVERDUE"
                              ? `${colors.error.main}15`
                              : colors.action.hover,
                          color:
                            reminder.reminder_status === "ACTIVE"
                              ? colors.success.main
                              : reminder.reminder_status === "PAUSED"
                              ? colors.warning.main
                              : reminder.reminder_status === "CANCELLED" ||
                                reminder.reminder_status === "COMPLETED"
                              ? colors.error.main
                              : reminder.reminder_status === "SENT"
                              ? colors.success.main
                              : reminder.reminder_status === "OVERDUE"
                              ? colors.error.main
                              : colors.text.secondary,
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          borderRadius: "4px",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: "100px", py: 1 }}>
                      {multiSelect.selectedCount > 0 ? (
                        <Box sx={{ width: "96px" }} />
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCommentClick(reminder);
                            }}
                            title="Comments"
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              "&:hover": {
                                backgroundColor: "transparent",
                                color: colors.primary.main,
                              },
                            }}>
                            <FiMessageSquare size={16} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/reminders/${reminder.id}?mode=edit`);
                            }}
                            title="Edit"
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              "&:hover": {
                                backgroundColor: "transparent",
                                color: colors.primary.main,
                              },
                            }}>
                            <FiEdit2 size={16} />
                          </IconButton>
                          {canDelete && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(reminder);
                              }}
                              title="Delete"
                              sx={{
                                color: colors.text.primary,
                                p: 1,
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: colors.error.main,
                                },
                              }}>
                              <FiTrash2 size={16} />
                            </IconButton>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bottom Pagination */}
      {!loading && reminders.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 2,
            gap: 1,
            opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
            pointerEvents: multiSelect.selectedCount > 0 ? "none" : "auto",
          }}>
          <IconButton
            size="small"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0 || multiSelect.selectedCount > 0}
            sx={{
              marginRight: -2,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: colors.action.hover,
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}>
            <FiChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: "100px",
              textAlign: "center",
            }}>
            Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() =>
              setPage(
                Math.min(Math.ceil(totalCount / rowsPerPage) - 1, page + 1)
              )
            }
            disabled={
              page >= Math.ceil(totalCount / rowsPerPage) - 1 ||
              multiSelect.selectedCount > 0
            }
            sx={{
              marginLeft: -2,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: colors.action.hover,
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}>
            <FiChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Single Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setReminderToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName="reminder"
        deleting={deleting}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        count={multiSelect.selectedCount}
        itemName="reminders"
      />

      {/* Comment Modal */}
      <CommentModal
        open={commentModalOpen}
        onClose={() => {
          setCommentModalOpen(false);
          setSelectedReminder(null);
        }}
        contactId={selectedReminder?.contact_id}
        contactName={selectedReminder ? getContactName(selectedReminder) : ""}
      />
    </Box>
  );
}
